import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, User } from 'lucide-react';
import api from '../../api';
import { useToast } from './ToastContainer';

const SubscriptionHistoryModal = ({ isOpen, onClose, tenantId, tenantName }) => {
  const { error: showError } = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Wrapped in useCallback to stabilize the function reference
  const loadHistory = useCallback(async () => {
    if (!tenantId) return;
    
    setLoading(true);
    try {
      const res = await api.get(`/api/super-admin/subscription/tenant/${tenantId}/history?limit=20&page=${page}`);
      if (res.data.success) {
        setHistory(res.data.data || []);
        setTotalPages(res.data.pagination?.pages || 1);
        setTotalItems(res.data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('❌ Error loading history:', err);
      showError(err.response?.data?.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, showError]);

  useEffect(() => {
    if (isOpen && tenantId) {
      loadHistory();
    }
  }, [isOpen, tenantId, page, loadHistory]); // Safely added loadHistory here

  if (!isOpen) return null;

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get action color
  const getActionColor = (action) => {
    const colors = {
      'subscription_created': 'text-emerald-600 bg-emerald-50 border-emerald-200',
      'subscription_updated': 'text-blue-600 bg-blue-50 border-blue-200',
      'subscription_cancelled': 'text-rose-600 bg-rose-50 border-rose-200',
      'subscription_renewed': 'text-indigo-600 bg-indigo-50 border-indigo-200',
      'trial_started': 'text-blue-600 bg-blue-50 border-blue-200',
      'trial_ended': 'text-amber-600 bg-amber-50 border-amber-200',
      'price_override': 'text-emerald-600 bg-emerald-50 border-emerald-200',
      'price_override_removed': 'text-rose-600 bg-rose-50 border-rose-200',
      'plan_created': 'text-purple-600 bg-purple-50 border-purple-200',
      'plan_updated': 'text-indigo-600 bg-indigo-50 border-indigo-200',
      'plan_deleted': 'text-rose-600 bg-rose-50 border-rose-200',
      'plan_changed': 'text-amber-600 bg-amber-50 border-amber-200',
    };
    return colors[action] || 'text-slate-600 bg-slate-50 border-slate-200';
  };

  // Get action label
  const getActionLabel = (action) => {
    const labels = {
      'subscription_created': 'Subscription Created',
      'subscription_updated': 'Subscription Updated',
      'subscription_cancelled': 'Subscription Cancelled',
      'subscription_renewed': 'Subscription Renewed',
      'trial_started': 'Trial Started',
      'trial_ended': 'Trial Ended',
      'price_override': 'Special Price Applied',
      'price_override_removed': 'Special Price Removed',
      'plan_created': 'Plan Created',
      'plan_updated': 'Plan Updated',
      'plan_deleted': 'Plan Deleted',
      'plan_changed': 'Plan Changed',
    };
    return labels[action] || action.replace(/_/g, ' ').toUpperCase();
  };

  // Get action description
  const getActionDescription = (item) => {
    if (item.description) return item.description;
    
    // Build description from changes
    if (item.previousState && item.currentState) {
      const changes = [];
      const prev = item.previousState;
      const curr = item.currentState;
      
      if (prev.planId && curr.planId && prev.planId !== curr.planId) {
        changes.push(`Plan: ${prev.planId} → ${curr.planId}`);
      }
      if (prev.status && curr.status && prev.status !== curr.status) {
        changes.push(`Status: ${prev.status} → ${curr.status}`);
      }
      if (prev.price !== undefined && curr.price !== undefined && prev.price !== curr.price) {
        changes.push(`Price: ₹${prev.price} → ₹${curr.price}`);
      }
      
      return changes.join(' • ') || 'Updated subscription';
    }
    
    return 'Action performed';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Subscription History
            </h2>
            <p className="text-sm text-slate-500">{tenantName || tenantId}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
                <p className="text-sm text-slate-500 mt-3">Loading history...</p>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-slate-700">No History Found</h3>
              <p className="text-xs text-slate-500 mt-1">No subscription actions have been recorded for this tenant.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item, index) => (
                <div
                  key={item._id || index}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getActionColor(item.action)}`}>
                          {getActionLabel(item.action)}
                        </span>
                        <span className="text-xs text-slate-400">
                          {formatDate(item.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1.5">
                        {getActionDescription(item)}
                      </p>
                      {item.performedBy?.userEmail && (
                        <p className="text-xs text-slate-400 mt-1">
                          <User className="w-3 h-3 inline mr-1" />
                          By: {item.performedBy.userEmail} ({item.performedBy.userType || 'system'})
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <span className="text-[10px] text-slate-400 font-mono">
                        #{String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  {/* Show changes if available */}
                  {item.previousState && item.currentState && (
                    <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500 grid grid-cols-1 md:grid-cols-2 gap-1">
                      <div>
                        <span className="font-medium">Before:</span>
                        {Object.entries(item.previousState).map(([key, value]) => (
                          <div key={key} className="truncate">
                            {key}: {value !== null && value !== undefined ? String(value) : '-'}
                          </div>
                        ))}
                      </div>
                      <div>
                        <span className="font-medium">After:</span>
                        {Object.entries(item.currentState).map(([key, value]) => (
                          <div key={key} className="truncate">
                            {key}: {value !== null && value !== undefined ? String(value) : '-'}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
              <span className="text-xs text-slate-500">
                Showing {history.length} of {totalItems} entries
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-xs font-medium text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between rounded-b-2xl">
          <span className="text-xs text-slate-400">
            Total {totalItems} history entries
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionHistoryModal;